import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Package, Plant, Truck, Cow } from "@phosphor-icons/react";
import { VerifiedBadge, ExportBadge } from "./Badges";
import { formatPrice, UNITS_LABEL } from "@/lib/format";

export default function ProductCard({ item, isExample }) {
    const covers = [
        item.photos?.[0],
        item.catalog_image?.status === "approved"
            ? (item.catalog_image.thumbnail_url || item.catalog_image.url)
            : null,
    ].filter(Boolean);
    const [failedImages, setFailedImages] = useState(0);
    const coversKey = covers.join("|");
    useEffect(() => { setFailedImages(0); }, [item.id, coversKey]);
    const cover = covers[failedImages];
    const listedPrice = Number(item.price);
    const hasPrice = Number.isFinite(listedPrice) && listedPrice > 0;
    const isEx = isExample || item.is_example;
    const CardTag = isEx ? "div" : Link;
    const cardProps = isEx
        ? { className: "block bg-white rounded-2xl overflow-hidden border-2 border-dashed border-[#E8A33A]/50 relative" }
        : { to: `/producto/${item.id}`, className: "card-lift block bg-white rounded-2xl overflow-hidden border border-border" };
    return (
        <CardTag {...cardProps} data-testid={`product-card-${item.id}`}>
            <div className={"bg-secondary relative overflow-hidden " + (cover ? "aspect-[16/9] sm:aspect-[4/3]" : "aspect-[16/7] sm:aspect-[4/3]")}>
                {cover ? (
                    <img src={cover} alt={item.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width="480" height="360" onError={() => setFailedImages((count) => count + 1)} />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#E7F1E2] via-[#F5F6EB] to-[#E9EEE5] grid place-items-center text-[#2E7D32]" data-testid="product-placeholder">
                        <div className="flex flex-col gap-2 items-center text-center px-3">
                            {/ganado|becerr|vaca|caballo|ovino|caprino/i.test(String(item.category || "") + " " + String(item.title || ""))
                                ? <Cow size={36} weight="duotone" aria-hidden="true" />
                                : /transporte|flete|camion/i.test(String(item.category || "") + " " + String(item.title || ""))
                                    ? <Truck size={36} weight="duotone" aria-hidden="true" />
                                    : /cultivo|fruta|verdura|hortaliza|grano|forraje|alfalfa/i.test(String(item.category || "") + " " + String(item.title || ""))
                                        ? <Plant size={36} weight="duotone" aria-hidden="true" />
                                        : <Package size={36} weight="duotone" aria-hidden="true" />}
                            <span className="text-xs font-semibold">Imagen no disponible</span>
                        </div>
                    </div>
                )}
                {isEx && (
                    <div className="absolute top-3 left-3 bg-[#E8A33A] text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full">
                        Ejemplo
                    </div>
                )}
                <div className="absolute top-3 left-3 flex gap-1.5" style={isEx ? { display: "none" } : {}}>
                    {item.seller?.verified && <VerifiedBadge />}
                    {item.export_ready && <ExportBadge />}
                </div>
            </div>
            <div className="p-4">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                    <div className="font-display font-bold text-lg text-foreground line-clamp-1">{item.title}</div>
                </div>
                <div className="text-2xl font-extrabold text-[#2E7D32] tracking-tight">
                    {item.pricing_model === "quote" ? "Solicitar cotización" : hasPrice ? formatPrice(item.price) : "Consultar precio"}
                    {hasPrice && item.pricing_model !== "quote" && <span className="text-xs font-medium text-muted-foreground ml-1">MXN</span>}
                    {hasPrice && item.pricing_model !== "quote" && <span className="text-sm font-medium text-muted-foreground ml-1">/ {UNITS_LABEL[item.unit] || item.unit}</span>}
                </div>
                {item.pricing_model === "wholesale" && item.wholesale_price && (
                    <div className="text-xs text-muted-foreground mt-1">Mayoreo: <span className="font-semibold text-foreground">{formatPrice(item.wholesale_price)}</span> desde {item.wholesale_min_qty} {UNITS_LABEL[item.unit] || item.unit}</div>
                )}
                {item.pricing_model === "tiered" && item.pricing_tiers?.length ? (
                    <div className="text-xs text-muted-foreground mt-1">Desde {formatPrice(Math.min(...item.pricing_tiers.map((t) => t.price)))}/{item.unit} al mayoreo</div>
                ) : null}
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
                    <span className="inline-flex items-center gap-1"><MapPin size={12} /> {item.city}</span>
                    <span>·</span>
                    <span>{item.quantity} {UNITS_LABEL[item.unit] || item.unit} disp.</span>
                </div>
                {item.seller?.business_name && !isEx && (
                    <div className="text-xs text-muted-foreground mt-2 truncate">
                        {item.seller.business_name}
                    </div>
                )}
            </div>
        </CardTag>
    );
}
